import React, { useState, useRef, useEffect, Fragment } from 'react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './OnboardingModal.scss';

/**
 * Reusable Onboarding Modal Component
 * 
 * @param {boolean} opened - Whether the modal is visible
 * @param {function} closeAction - Callback to close the modal
 * @param {array} slides - Array of slide objects with { title, description, content }
 * @param {string} skipLabel - Label for skip button (default: "SKIP")
 * @param {string} nextLabel - Label for next button (default: "NEXT")
 * @param {string} finishLabel - Label for finish button (default: "FINISH")
 * @param {function} onFinish - Optional callback for finish button (defaults to closeAction)
 */
const OnboardingModal = ({
    opened,
    closeAction,
    slides = [],
    skipLabel = "SKIP",
    nextLabel = "NEXT",
    finishLabel = "FINISH",
    onFinish
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const sliderRef = useRef(null);
    const bodyRef = useRef(null);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);

    const totalSlides = slides.length;
    const isLastSlide = currentSlide === totalSlides - 1;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (e.target.classList.contains('onboarding-overlay')) {
                closeAction();
            }
        };
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, [closeAction]);

    useEffect(() => {
        if (opened) {
            // Reset to first slide when opening
            setCurrentSlide(0);
            if (sliderRef.current) {
                sliderRef.current.slickGoTo(0);
            }
            // Trigger a resize event to recalculate slider dimensions after mount
            const timer = setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
                checkScroll();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [opened]);

    const checkScroll = () => {
        if (bodyRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
            const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;
            setShowScrollIndicator(!isAtBottom);
        }
    };

    useEffect(() => {
        const body = bodyRef.current;
        if (body) {
            body.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
            checkScroll();
        }
        return () => {
            if (body) {
                body.removeEventListener('scroll', checkScroll);
            }
            window.removeEventListener('resize', checkScroll);
        };
    }, [opened, currentSlide]);

    useEffect(() => {
        const timer = setTimeout(checkScroll, 300);
        checkScroll();
        return () => clearTimeout(timer);
    }, [currentSlide]);

    const settings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        adaptiveHeight: true,
        beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex)
    };

    const handleNext = () => {
        if (!isLastSlide) {
            sliderRef.current.slickNext();
        } else {
            // Use onFinish callback if provided, otherwise fallback to closeAction
            (onFinish || closeAction)();
        }
    };

    const handlePrev = () => {
        sliderRef.current.slickPrev();
    };

    const goToSlide = (index) => {
        sliderRef.current.slickGoTo(index);
    };

    if (!opened || totalSlides === 0) return null;

    return (
        <Fragment>
            <div className="onboarding-overlay tw-fixed tw-inset-0 tw-z-[1002] tw-flex tw-justify-center tw-items-center">
                {/* Left Arrow - Outside Modal */}
                <button
                    onClick={handlePrev}
                    disabled={currentSlide === 0}
                    className={`onboarding-arrow-outside prev ${currentSlide === 0 ? 'disabled' : ''}`}
                >
                    <i className="chevron left icon"></i>
                </button>

                {/* Modal Container */}
                <div className="onboarding-modal-container">
                    {/* Close Button */}
                    <div className="tw-absolute tw-top-4 tw-right-4 tw-cursor-pointer tw-z-10" onClick={closeAction}>
                        <i className="plus icon tw-rotate-45 tw-transform tw-text-gray-400"></i>
                    </div>

                    {/* Scrollable Body Content */}
                    <div className="onboarding-modal-body" ref={bodyRef}>
                        <div className="onboarding-modal-content">
                            <Slider ref={sliderRef} {...settings}>
                                {slides.map((slide, index) => (
                                    <div key={index} className="onboarding-slide">
                                        <h2 className="tw-text-main1 tw-text-[28px] tw-font-semibold tw-mb-2">{slide.title}</h2>
                                        <p className="tw-text-[16px] tw-leading-[150%] tw-text-main1 tw-font-normal tw-mb-4">{slide.description}</p>
                                        {slide.content}
                                    </div>
                                ))}
                            </Slider>
                        </div>
                    </div>

                    {/* Fixed Footer */}
                    <div className="onboarding-modal-footer">
                        <div className={`scroll-indicator ${showScrollIndicator ? 'visible' : ''}`}></div>
                        <div className="tw-flex tw-justify-center tw-mb-6">
                            <ul className="onboarding-dots">
                                {slides.map((_, index) => (
                                    <li key={index} onClick={() => goToSlide(index)}>
                                        <div className={`onboarding-dot ${index === currentSlide ? 'active' : ''}`} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="tw-flex tw-justify-end tw-items-center tw-gap-4">
                            <span
                                onClick={closeAction}
                                className="tw-cursor-pointer tw-text-main1 hover:tw-text-main1 tw-border-none tw-font-medium tw-uppercase tw-text-xl"
                            >
                                {skipLabel}
                            </span>
                            <button
                                onClick={handleNext}
                                className="tw-bg-color1 tw-text-white tw-px-8 tw-py-3 tw-rounded-full tw-font-medium tw-uppercase tw-text-xl hover:tw-opacity-90 tw-border-none tw-cursor-pointer"
                            >
                                {isLastSlide ? finishLabel : nextLabel}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Arrow - Outside Modal */}
                <button
                    onClick={handleNext}
                    className={`onboarding-arrow-outside next ${isLastSlide ? 'disabled' : ''}`}
                >
                    <i className="chevron right icon"></i>
                </button>
            </div>
            <div className="tw-opacity-25 tw-fixed tw-inset-0 tw-z-[1001] tw-bg-black"></div>
        </Fragment>
    );
};

export default OnboardingModal;
